import { Input, Radio, Space, FormProps, Checkbox } from 'antd';
import { Trans, useTranslation } from 'react-i18next';

import useAPIQuery from '../api/useAPIQuery';
import Form from './Form';

export interface AccessProfileFormValues {
  name: string;
  viewAllVacancies: boolean;
  createAndEditVacancies: boolean;
  accessTalentPool: boolean;
  accessConfigurations: boolean;
  accessReports: boolean;
  viewRequestJobPosting: string;
  createAndEditRequestJobPosting: boolean;
  canApproveRequestJobPosting: boolean;
  approveRequestJobPostingPermission: 'FORBID' | 'MARKED' | 'ALL';
}

const AccessProfileForm = ({ ...props }: FormProps) => {
  const { t } = useTranslation();

  const viewAllVacanciesValue = Form.useWatch<AccessProfileFormValues['viewAllVacancies']>(
    'viewAllVacancies',
    props.form
  );

  const viewRequestJobPostingValue = Form.useWatch<
    AccessProfileFormValues['viewRequestJobPosting']
  >('viewRequestJobPosting', props.form);

  const config = useAPIQuery('/configs/tenant-configurations/');
  const enableNewApproverProfile = config.data?.t010_enableNewApproverProfile;

  return (
    <Form<AccessProfileFormValues> {...props}>
      <Form.Item
        label={t('Nome')}
        name="name"
        rules={[
          { required: true },
          {
            validator: (rule, value: string) => {
              if (value === 'Padrão' || value === 'Default' || value === 'Predeterminado') {
                return Promise.reject(
                  new Error(
                    t(
                      'O nome "Padrão" é reservado para uso do sistema. Por favor, escolha outro nome para o perfil.'
                    )
                  )
                );
              }

              return Promise.resolve();
            },
          },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Legend>
        <Trans>Permissões</Trans>:
      </Form.Legend>
      <Form.Item
        shouldUpdate={(prev, next) => prev.viewAllVacancies !== next.viewAllVacancies}
        noStyle
      >
        {(form) => (
          <Form.Item
            label={t('Acesso às vagas')}
            name="viewAllVacancies"
            rules={[{ required: true }]}
          >
            <Radio.Group
              onChange={(e) => {
                if (!e.target.value) {
                  form.setFieldsValue({ accessReports: false });
                }
              }}
            >
              <Space direction="vertical">
                <Radio value={false}>
                  <Trans>Visualizar apenas vagas marcadas</Trans>
                </Radio>
                <Radio value>
                  <Trans>Visualizar todas as vagas</Trans>
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
        )}
      </Form.Item>
      <Form.Item
        label={t('Acesso às requisições')}
        name="viewRequestJobPosting"
        rules={[{ required: true }]}
      >
        <Radio.Group
          onChange={(e) => {
            if (e.target.value === 'FORBID') {
              props.form?.setFieldsValue({
                createAndEditRequestJobPosting: false,
                canApproveRequestJobPosting: false,
              });
            }
            props.form?.setFieldsValue({
              approveRequestJobPostingPermission: null,
            });
          }}
        >
          <Space direction="vertical">
            <Radio value="FORBID">
              <Trans>Sem acesso a requisições</Trans>
            </Radio>
            <Radio value="MARKED">
              <Trans>Visualizar apenas as requisições marcadas</Trans>
            </Radio>
            <Radio value="ALL">
              <Trans>Visualizar todas as requisições</Trans>
            </Radio>
          </Space>
        </Radio.Group>
      </Form.Item>
      {enableNewApproverProfile && (
        <Form.Item
          label={t('Nível de aprovação')}
          name="approveRequestJobPostingPermission"
          rules={[{ required: true }]}
        >
          <Radio.Group
            onChange={(e) => {
              if (e.target.value === 'FORBID') {
                props.form?.setFieldsValue({
                  createAndEditRequestJobPosting: false,
                });
              }
            }}
          >
            <Space direction="vertical">
              <Radio value="FORBID">
                <Trans>Não pode aprovar requisições</Trans>
              </Radio>
              <Radio value="MARKED" disabled={viewRequestJobPostingValue === 'FORBID'}>
                <Trans>Aprovar apenas requisições marcadas</Trans>
              </Radio>
              <Radio
                value="ALL"
                disabled={
                  viewRequestJobPostingValue === 'FORBID' || viewRequestJobPostingValue === 'MARKED'
                }
              >
                <Trans>Aprovar todas as requisições</Trans>
              </Radio>
            </Space>
          </Radio.Group>
        </Form.Item>
      )}
      <Form.Item label={t('Acesso às funcionalidades')}>
        <Space direction="vertical">
          <Form.Item name="createAndEditVacancies" valuePropName="checked" noStyle>
            <Checkbox>
              <Trans>Criação e Edição de Vagas</Trans>
            </Checkbox>
          </Form.Item>
          <Form.Item name="accessTalentPool" valuePropName="checked" noStyle>
            <Checkbox>
              <Trans>Banco de Talentos</Trans>
            </Checkbox>
          </Form.Item>
          <Form.Item name="accessConfigurations" valuePropName="checked" noStyle>
            <Checkbox>
              <Trans>Configurações</Trans>
            </Checkbox>
          </Form.Item>
          <Form.Item name="accessReports" valuePropName="checked" noStyle>
            <Checkbox disabled={!viewAllVacanciesValue}>
              <Trans>Relatórios</Trans>
            </Checkbox>
          </Form.Item>
          <Form.Item name="createAndEditRequestJobPosting" valuePropName="checked" noStyle>
            <Checkbox
              disabled={!viewRequestJobPostingValue || viewRequestJobPostingValue === 'FORBID'}
            >
              <Trans>Criação e Edição de requisições</Trans>
            </Checkbox>
          </Form.Item>
          {!enableNewApproverProfile && (
            <Form.Item name="canApproveRequestJobPosting" valuePropName="checked" noStyle>
              <Checkbox
                disabled={!viewRequestJobPostingValue || viewRequestJobPostingValue === 'FORBID'}
              >
                <Trans>Aprovar requisições</Trans>
              </Checkbox>
            </Form.Item>
          )}
        </Space>
      </Form.Item>
    </Form>
  );
};

export default AccessProfileForm;
